import { type FC } from 'react';

/**
 * Service cards and gallery tiles share the same 1/2/3-column grid (gap-5)
 * inside the 72rem container, so they share a `sizes`: 349px once the
 * container stops growing, a third of the viewport above `lg`, half below
 * that, and full width on a phone.
 */
export const CARD_PHOTO_SIZES =
  '(min-width: 1152px) 349px, (min-width: 1024px) calc(33.3vw - 35px), (min-width: 640px) calc(50vw - 42px), calc(100vw - 40px)';

type PhotoProps = {
  /** An image imported with imagetools' `?w=...&as=picture` directive. */
  photo: ImagetoolsPicture;
  alt: string;
  /**
   * The image's layout width at each breakpoint. The browser picks a variant
   * from the srcset with this, so a wrong value here costs bandwidth or
   * sharpness even though the layout still looks right.
   */
  sizes: string;
  className?: string;
  /** Anything above the fold should be 'eager'; everything else stays lazy. */
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'low' | 'auto';
  decorative?: boolean;
};

/**
 * A landing-page photo served at a size that suits the slot it lands in.
 *
 * The intrinsic width/height come from the generated variant, so the browser
 * reserves the right box before the bytes arrive even where the layout has no
 * aspect-ratio wrapper of its own.
 */
export const Photo: FC<PhotoProps> = ({
  photo,
  alt,
  sizes,
  className,
  loading = 'lazy',
  fetchPriority,
  decorative = false
}) => (
  <img
    src={photo.img.src}
    srcSet={photo.sources.webp}
    sizes={sizes}
    width={photo.img.w}
    height={photo.img.h}
    alt={decorative ? '' : alt}
    aria-hidden={decorative || undefined}
    loading={loading}
    fetchPriority={fetchPriority}
    className={className}
  />
);
